import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { i18next, dialogs } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import segment from 'src/js/modules/analytics/segment';
import triggerIds from 'src/js/modules/website-design/constants/trigger-ids';
import getSurfaceName from 'src/js/modules/website-design/helpers/get-surface-name';
import displayOptionTypes from '../constants/display-options-type';
import AsyncDropdownItem from './async-dropdown-item';
import BlockSettingsSlider from './block-settings-slider';
import ContainerWidthOption from './container-width-option';

const POPOVER_WIDTH = 232;

const getCaptions = () => ({
  addColorSchemeTooltipText: i18next.t('Add color scheme'),
  hideElementWarning: i18next.t('Element is hidden. You can always bring it back using checkbox'),
  toggleLabels: {
    expanded: i18next.t('Show less'),
    collapsed: i18next.t('Show more'),
  },
});

const {
  Box,
  TabNonSwipeableArea,
  Heading,
  Paragraph,
  Checkbox,
  RadioButtonGroup,
  RadioButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ToggleSection,
  ColorTileSelector,
} = designSystem;

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const getTitleBoxMarginTop = (prevItem, prevItemWithDivider) => {
  if (prevItemWithDivider || !prevItem) {
    return '';
  }

  return 'spacing-s';
};

const getSubtitleBoxMarginTop = (item, prevItem, prevItemWithDivider) => {
  if (item.title) {
    return 'spacing-4-xs';
  }

  if (prevItemWithDivider || !prevItem) {
    return '';
  }

  return 'spacing-s';
};

const getControlBoxMarginTop = (item, prevItem, prevItemWithDivider) => {
  if (item.title || item.subtitle) {
    return 'spacing-2-xs';
  }

  if (prevItemWithDivider || !prevItem) {
    return '';
  }

  if (prevItem.type === item.type) {
    switch (item.type) {
      case displayOptionTypes.CHECKBOX:
        return 'spacing-3-xs';
      default:
        return 'spacing-2-xs';
    }
  }

  return 'spacing-2-xs';
};

function BlockSettingsOption({
  item,
  prevItem = null,
  keySalt,
  prevItemWithDivider,
  updateSharedData,
  currentPalette = null,
}) {
  const { id, type } = item;

  const hiddenChildRef = useRef(null);

  const checkboxOptionClasses = classNames('ws-block-settings-dialog__checkbox-option', {
    'ws-block-settings-dialog__checkbox-option--highlighted': item.highlighted,
  });

  const captions = getCaptions();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (item.highlighted) {
      hiddenChildRef.current.scrollIntoView();

      const clickHandler = () => item.onClick(item.isEnabled, item, { highlighted: false });

      document.addEventListener('click', clickHandler, { once: true });

      return () => document.removeEventListener('click', clickHandler);
    }
  }, [item]);

  const onSchemeSettingsClick = (optionId) => {
    trackEvent(events.BLOCK_SETTINGS_COLOR_SCHEME_SETTINGS_CLICKED, {
      colorScheme: optionId,
    });
    updateSharedData();
    dialogs.operations.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
      colorSchemeId: optionId,
      triggerId: triggerIds.BLOCK_SETTINGS,
    });
  };

  const onAddColorScheme = (surfaceIndex = 0) => {
    trackEvent(events.ADD_COLOR_SCHEME_CLICKED, {
      triggerId: triggerIds.BLOCK_SETTINGS,
    });
    updateSharedData();
    dialogs.operations.show(dialogTypes.ADD_COLOR_SCHEME_DIALOG, {
      triggerId: triggerIds.BLOCK_SETTINGS,
      surfaceIndex,
    });
  };

  const titleBoxMarginTop = getTitleBoxMarginTop(prevItem, prevItemWithDivider);
  const titleBoxProps = {
    ...(titleBoxMarginTop && { marginTop: titleBoxMarginTop }),
  };
  const subtitleBoxMarginTop = getSubtitleBoxMarginTop(item, prevItem, prevItemWithDivider);
  const subtitleBoxProps = {
    ...(subtitleBoxMarginTop && { marginTop: subtitleBoxMarginTop }),
  };
  const controlBoxMarginTop = getControlBoxMarginTop(item, prevItem, prevItemWithDivider);
  const controlBoxProps = {
    ...(controlBoxMarginTop && { marginTop: controlBoxMarginTop }),
  };

  return (
    <React.Fragment>
      {Boolean(item.title) && (
        <Box {...titleBoxProps}>
          {item.toggleable ? (
            <ToggleSection
              key={`${id}-switcher`}
              title={item.title}
              value={Boolean(item.value)}
              onChange={(isEnabled) => item.onToggle(isEnabled, item)}
            />
          ) : (
            <Heading type="heading-6">{item.title}</Heading>
          )}
        </Box>
      )}

      {Boolean(item.subtitle) && (
        <Box {...subtitleBoxProps}>
          <Paragraph appearance="medium-emphasis" size="small">
            {item.subtitle}
          </Paragraph>
        </Box>
      )}

      <Box {...controlBoxProps}>
        {type === displayOptionTypes.TOGGLE && !item.disableChange && (
          <ToggleSection
            key={id}
            title={item.label}
            defaultValue={!item.isEnabled}
            onChange={() => item.onClick(!item.isEnabled, item)}
          />
        )}

        {type === displayOptionTypes.CHECKBOX && !item.disableChange && (
          <div className={checkboxOptionClasses} ref={hiddenChildRef}>
            <Popover
              isOpen={item.highlighted}
              width={POPOVER_WIDTH}
              placement="top-center"
              appearance="dark"
              mode="popover"
              description={captions.hideElementWarning}
            >
              <PopoverTrigger>
                <Checkbox
                  key={id}
                  id={id}
                  value={id}
                  label={item.label}
                  checked={item.isEnabled}
                  onChange={() => item.onClick(!item.isEnabled, item)}
                />
              </PopoverTrigger>
              <PopoverContent />
            </Popover>
          </div>
        )}

        {type === displayOptionTypes.RADIO && (
          <RadioButtonGroup
            name={id}
            selectedValue={item.value}
            onChange={(value) => item.onClick(item, value)}
          >
            {item.options.map((option) => (
              <RadioButton
                key={`${id}-${option.value}-${keySalt}`}
                id={`${id}-${option.value}`}
                value={option.value}
                label={option.label}
                isLabelBold
              />
            ))}
          </RadioButtonGroup>
        )}

        {type === displayOptionTypes.SURFACE && item.value && (
          <ColorTileSelector
            key={item.id}
            options={item.options.map((option) => ({
              id: option.value,
              backgroundColor: option.backgroundColor,
              isDarkTheme: option.isDarkTheme,
              tooltipText: getSurfaceName(option.value, currentPalette?.schemes) || option.label,
            }))}
            value={item.value}
            onChange={(selectedSurface) => item.onClick(item, selectedSurface)}
            onConfigureClick={onSchemeSettingsClick}
            onAddButtonClick={() => onAddColorScheme(item.index)}
            addButtonTooltipText={captions.addColorSchemeTooltipText}
            toggleLabels={captions.toggleLabels}
          />
        )}

        {type === displayOptionTypes.SLIDER && (
          <TabNonSwipeableArea>
            <BlockSettingsSlider
              itemId={item.id}
              min={Number(item.min)}
              max={Number(item.max)}
              step={Number(item.step)}
              value={Number(item.value)}
              valueLabelDisplay={item.displayCurrentValue ? 'off' : 'on'}
              onChange={(value) => item.onChange(item, value)}
              onAfterChange={(value) => item.onAfterChange(item, value)}
            />
          </TabNonSwipeableArea>
        )}

        {type === displayOptionTypes.BREAKPOINTS_SLIDER && (
          <TabNonSwipeableArea>
            <BlockSettingsSlider
              itemId={item.id}
              min={Number(item.values[0])}
              max={Number(item.values[item.values.length - 1])}
              step={Number(item.values[1]) - Number(item.values[0])}
              value={Number(item.value)}
              valueLabelDisplay="on"
              onChange={(value) => item.onChange(item, String(Math.round(value * 10) / 10))}
              onAfterChange={(value) =>
                item.onAfterChange(item, String(Math.round(value * 10) / 10))
              }
            />
          </TabNonSwipeableArea>
        )}

        {type === displayOptionTypes.ONLINE_STORE_CATEGORY && (
          <AsyncDropdownItem
            loadOptions={item.loadOptions}
            value={item.value}
            placeholder=""
            onChange={(option) => item.onChange(item, option.value)}
          />
        )}

        {type === displayOptionTypes.ONLINE_STORE_CLEAN_URL_SELECTOR && (
          <Checkbox
            key={id}
            id={id}
            label={item.label}
            {...(item.annotation && { description: item.annotation })}
            checked={item.isEnabled}
            onChange={(event) => item.onChange(event, item)}
          />
        )}

        {type === displayOptionTypes.CONTAINER_WIDTH && (
          <ContainerWidthOption
            value={item.value}
            customValue={item.customValue}
            onClick={(value) => item.onClick(item, value)}
            onChange={(value) => item.onChange(item, value)}
            onAfterChange={(value) => item.onAfterChange(item, value)}
          />
        )}
      </Box>
    </React.Fragment>
  );
}

BlockSettingsOption.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    annotation: PropTypes.string,
    label: PropTypes.string,
    displayCurrentValue: PropTypes.bool,
    type: PropTypes.string.isRequired,
    isEnabled: PropTypes.bool,
    disableChange: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
    values: PropTypes.arrayOf(PropTypes.string), // available values for breakpoints slider from API
    onClick: PropTypes.func,
    onChange: PropTypes.func,
    onToggle: PropTypes.func,
    onAfterChange: PropTypes.func,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
    src: PropTypes.string,
    blockNode: PropTypes.object,
    highlighted: PropTypes.bool,
    toggleable: PropTypes.bool,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        onClick: PropTypes.func,
      })
    ),
    loadOptions: PropTypes.func,
    index: PropTypes.number,
    customValue: PropTypes.string,
  }).isRequired,
  prevItem: PropTypes.oneOfType([
    PropTypes.shape({
      isGrouped: PropTypes.bool,
      type: PropTypes.string,
    }),
    PropTypes.oneOf([null]),
  ]),
  keySalt: PropTypes.string.isRequired,
  prevItemWithDivider: PropTypes.bool.isRequired,
  updateSharedData: PropTypes.func.isRequired,
  currentPalette: PropTypes.object,
};

export default BlockSettingsOption;
